import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useAuth } from "../../app/AuthContext";
import type {
  JsonObject,
  KnowledgeCardDetail,
  RetrievalDebugResponse,
  RetrievalProfileDetailRead,
  RetrievalProfileRead,
  RetrievalProfileUpdate,
} from "../../shared/api/types";
import { collectPermissions } from "../../shared/lib/permissions";
import { formatDateTime } from "../../shared/lib/format";
import { Badge, Button, Callout, Card, EmptyState, Field, Input, Modal, Spinner, Textarea } from "../../shared/ui/Primitives";
import { JsonView } from "../../shared/ui/JsonView";
import { useToast } from "../../shared/ui/Toast";
import { CardDetail } from "../knowledge/CardDetail";
import { defaultProfileConfig } from "./defaultProfile";

const RetrievalGraph = lazy(async () => {
  const module = await import("./RetrievalGraph");
  return { default: module.RetrievalGraph };
});

export function RetrievalScreen() {
  const { api, user } = useAuth();
  const permissions = useMemo(() => collectPermissions(user), [user]);
  const { showToast } = useToast();
  const [profiles, setProfiles] = useState<RetrievalProfileRead[]>([]);
  const [profileId, setProfileId] = useState("");
  const [profileDetail, setProfileDetail] = useState<RetrievalProfileDetailRead | null>(null);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<RetrievalDebugResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [loadingProfileDetail, setLoadingProfileDetail] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(0);
  const [graphExpanded, setGraphExpanded] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<KnowledgeCardDetail | null>(null);
  const [selectedCardLoading, setSelectedCardLoading] = useState(false);
  const profileDetailRequest = useRef(0);

  const loadProfiles = useCallback(async () => {
    setLoadingProfiles(true);
    try {
      const current = await api.listRetrievalProfiles();
      setProfiles(current);
      setProfileId((existing) => (
        existing && current.some((item) => item.id === existing)
          ? existing
          : current.find((item) => item.is_default)?.id || current.find((item) => item.is_active)?.id || ""
      ));
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось загрузить профили", "danger");
    } finally {
      setLoadingProfiles(false);
    }
  }, [api, showToast]);

  const loadProfileDetail = useCallback(async (id: string) => {
    const requestId = ++profileDetailRequest.current;
    if (!id || !permissions.has("settings.view")) {
      setProfileDetail(null);
      setLoadingProfileDetail(false);
      return;
    }
    setLoadingProfileDetail(true);
    setProfileDetail(null);
    try {
      const detail = await api.getRetrievalProfile(id);
      if (requestId === profileDetailRequest.current) setProfileDetail(detail);
    } catch (error) {
      if (requestId === profileDetailRequest.current) {
        setProfileDetail(null);
        showToast(error instanceof Error ? error.message : "Не удалось загрузить настройки профиля", "danger");
      }
    } finally {
      if (requestId === profileDetailRequest.current) setLoadingProfileDetail(false);
    }
  }, [api, permissions, showToast]);

  useEffect(() => { void loadProfiles(); }, [loadProfiles]);
  useEffect(() => { void loadProfileDetail(profileId); }, [loadProfileDetail, profileId]);

  useEffect(() => {
    if (!selectedCardId || !result) {
      setSelectedCard(null);
      setSelectedCardLoading(false);
      return;
    }
    let cancelled = false;
    setSelectedCardLoading(true);
    setSelectedCard(null);
    api.getCard(selectedCardId, result.snapshot_id)
      .then((detail) => { if (!cancelled) setSelectedCard(detail); })
      .catch((error) => {
        if (!cancelled) showToast(error instanceof Error ? error.message : "Карточка не найдена", "danger");
      })
      .finally(() => { if (!cancelled) setSelectedCardLoading(false); });
    return () => { cancelled = true; };
  }, [api, result, selectedCardId, showToast]);

  async function run(event: FormEvent) {
    event.preventDefault();
    if (!query.trim()) return;
    setRunning(true);
    setGraphExpanded(false);
    setResult(null);
    setSelectedItem(0);
    try {
      setResult(await api.debugRetrieval(query.trim(), profileId || null));
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Поиск завершился ошибкой", "danger");
    } finally {
      setRunning(false);
    }
  }

  async function saveProfile(profile: RetrievalProfileRead, payload: RetrievalProfileUpdate) {
    await api.updateRetrievalProfile(profile.id, payload);
    await loadProfiles();
    await loadProfileDetail(profile.id);
    showToast("Профиль обновлён", "success");
  }

  function updateProfileFromToolbar(profile: RetrievalProfileRead, payload: RetrievalProfileUpdate) {
    void saveProfile(profile, payload).catch((error) => {
      showToast(error instanceof Error ? error.message : "Не удалось обновить профиль", "danger");
    });
  }

  const selectedProfile = profiles.find((item) => item.id === profileId) ?? null;
  const selectedResultItem = result?.items[selectedItem] ?? null;

  function selectResultFromGraph(externalId: string) {
    const index = result?.items.findIndex((item) => item.external_id === externalId) ?? -1;
    if (index >= 0) setSelectedItem(index);
  }

  return (
    <div className="section-page retrieval-page">
      <header className="section-header">
        <div><h1>Лаборатория поиска</h1><p>Проверка точного, полнотекстового и векторного поиска с расширением по связям.</p></div>
        {permissions.has("settings.manage") ? <Button icon="plus" onClick={() => setCreateOpen(true)}>Новый профиль</Button> : null}
      </header>
      <div className="retrieval-layout">
        <aside className="profile-panel">
          <header><h2>Профили</h2><span>{profiles.length}</span></header>
          {loadingProfiles ? <div className="centered"><Spinner /></div> : null}
          {profiles.map((profile) => (
            <button
              type="button"
              key={profile.id}
              className={profile.id === profileId ? "profile-item is-active" : "profile-item"}
              onClick={() => setProfileId(profile.id)}
            >
              <div><strong>{profile.name}</strong>{profile.is_default ? <Badge tone="info">По умолчанию</Badge> : null}</div>
              <p>{profile.description || "Без описания"}</p>
              <span>{profile.is_active ? "Активен" : "Отключён"} · {formatDateTime(profile.updated_at)}</span>
            </button>
          ))}
          {permissions.has("settings.manage") && selectedProfile ? (
            <div className="profile-actions">
              <Button size="small" variant="secondary" disabled={selectedProfile.is_default} onClick={() => updateProfileFromToolbar(selectedProfile, { is_default: true })}>По умолчанию</Button>
              <Button size="small" variant="secondary" disabled={!profileDetail} onClick={() => setEditOpen(true)}>Редактировать</Button>
              <Button size="small" variant="ghost" onClick={() => updateProfileFromToolbar(selectedProfile, { is_active: !selectedProfile.is_active })}>{selectedProfile.is_active ? "Отключить" : "Включить"}</Button>
            </div>
          ) : null}
          {loadingProfileDetail ? <div className="centered centered--compact"><Spinner /><span>Читаем конфигурацию…</span></div> : null}
          {profileDetail ? (
            <div className="profile-config-preview">
              <div className="section-title-row section-title-row--compact">
                <strong>Версия конфигурации</strong>
                <Badge tone="info">v{profileDetail.latest_version.version_number}</Badge>
              </div>
              <span>{formatDateTime(profileDetail.latest_version.created_at)}</span>
              <details>
                <summary>Показать JSON</summary>
                <JsonView value={profileDetail.latest_version.config} />
              </details>
            </div>
          ) : null}
        </aside>

        <section className="retrieval-workbench">
          <Card>
            <form className="retrieval-query" onSubmit={run}>
              <Field label="Тестовый вопрос"><Textarea rows={4} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Например: Предохранитель 63 А означает SCCR 63 кА?" /></Field>
              <div className="retrieval-query__footer"><span>{selectedProfile?.name || "Профиль по умолчанию"}</span><Button type="submit" icon="search" disabled={!query.trim() || running}>{running ? "Ищем…" : "Запустить поиск"}</Button></div>
            </form>
          </Card>
          {running ? <Card className="centered"><Spinner /><span>Получаем кандидатов и обходим связи…</span></Card> : null}
          {!result && !running ? <EmptyState title="Введите контрольный вопрос" description="Результат покажет стартовые карточки, карточки по связям и собранный контекст." icon="flask" /> : null}
          {result ? (
            <>
              <div className="retrieval-summary">
                <Card><span>Карточек в результате</span><strong>{result.items.length}</strong></Card>
                <Card><span>Снимок базы</span><strong>{result.snapshot_id.slice(0, 8)}</strong></Card>
                <Card><span>Профиль</span><strong>{result.profile_version_id.slice(0, 8)}</strong></Card>
              </div>
              <Card><h2>Нормализованный запрос</h2><JsonView value={result.normalized_query} /></Card>
              <div className="retrieval-results">
                <Card className="retrieval-result-list">
                  <header><h2>Найденные сущности</h2><Badge tone="info">{result.items.length}</Badge></header>
                  {result.items.map((item, index) => (
                    <button
                      type="button"
                      key={`${item.card_version_id}-${item.chunk_id ?? index}`}
                      className={selectedItem === index ? "is-active" : ""}
                      onClick={() => setSelectedItem(index)}
                    >
                      <div><strong>{item.external_id}</strong><Badge>{item.source_channel}</Badge></div>
                      <span>{item.title}</span>
                      <small>Оценка: {item.score.toFixed(4)}</small>
                    </button>
                  ))}
                </Card>
                <Card className="retrieval-result-detail">
                  <h2>Сведения о результате</h2>
                  {selectedResultItem ? (
                    <>
                      <div className="result-heading">
                        <button type="button" className="knowledge-link knowledge-link--large" onClick={() => setSelectedCardId(selectedResultItem.external_id)}>{selectedResultItem.external_id}</button>
                        <strong>{selectedResultItem.title}</strong>
                      </div>
                      <dl className="meta-list">
                        <div><dt>Канал</dt><dd>{selectedResultItem.source_channel}</dd></div>
                        <div><dt>Оценка</dt><dd>{selectedResultItem.score}</dd></div>
                        <div><dt>Chunk ID</dt><dd>{selectedResultItem.chunk_id || "—"}</dd></div>
                      </dl>
                      <h3>Путь по связям</h3><JsonView value={selectedResultItem.relation_path} />
                    </>
                  ) : <p className="muted">Выберите строку слева.</p>}
                </Card>
              </div>
              <Card className="retrieval-graph-card">
                {!graphExpanded ? (
                  <Suspense fallback={<div className="centered retrieval-graph__loading"><Spinner /><span>Строим граф найденного контекста…</span></div>}>
                    <RetrievalGraph
                      items={result.items}
                      selectedExternalId={selectedResultItem?.external_id ?? null}
                      onSelect={selectResultFromGraph}
                      onOpenCard={setSelectedCardId}
                      onExpand={() => setGraphExpanded(true)}
                    />
                  </Suspense>
                ) : null}
              </Card>
              <Card><details open><summary>Итоговый контекст для модели</summary><pre className="context-preview">{result.context_text}</pre></details></Card>
              <Card><details><summary>Статистика выполнения</summary><JsonView value={result.stats} /></details></Card>
            </>
          ) : null}
        </section>
      </div>

      <Modal
        open={graphExpanded && Boolean(result)}
        title="Граф найденного контекста"
        onClose={() => { if (!selectedCardId) setGraphExpanded(false); }}
        wide
        className="retrieval-graph-modal"
      >
        {graphExpanded && result ? (
          <Suspense fallback={<div className="centered retrieval-graph__loading"><Spinner /><span>Разворачиваем граф…</span></div>}>
            <RetrievalGraph
              items={result.items}
              selectedExternalId={selectedResultItem?.external_id ?? null}
              onSelect={selectResultFromGraph}
              onOpenCard={setSelectedCardId}
              expanded
            />
          </Suspense>
        ) : null}
      </Modal>

      <Modal open={Boolean(selectedCardId)} title={selectedCard?.title || selectedCardId || "Карточка"} onClose={() => setSelectedCardId(null)} wide>
        {selectedCardLoading ? <div className="centered"><Spinner /></div> : selectedCard ? <CardDetail detail={selectedCard} onOpenCard={setSelectedCardId} /> : null}
      </Modal>

      <CreateProfileModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={async (payload) => {
          const created = await api.createRetrievalProfile(payload);
          await loadProfiles();
          setProfileId(created.id);
          setCreateOpen(false);
          showToast("Профиль создан", "success");
        }}
      />
      <EditProfileModal
        open={editOpen}
        profile={profileDetail}
        onClose={() => setEditOpen(false)}
        onSave={async (payload) => {
          if (!selectedProfile) return;
          await saveProfile(selectedProfile, payload);
          setEditOpen(false);
        }}
      />
    </div>
  );
}

function CreateProfileModal({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: { name: string; description: string | null; is_default: boolean; config: JsonObject }) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [json, setJson] = useState(() => JSON.stringify(defaultProfileConfig, null, 2));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const config = parseConfig(json, setError);
    if (!config) return;
    setSaving(true);
    try {
      await onCreate({ name: name.trim(), description: description.trim() || null, is_default: isDefault, config });
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Не удалось создать профиль");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} title="Новый профиль поиска" onClose={onClose} wide footer={<><Button variant="secondary" onClick={onClose}>Отмена</Button><Button form="create-profile-form" type="submit" disabled={!name.trim() || saving}>{saving ? "Создаём…" : "Создать"}</Button></>}>
      <form id="create-profile-form" className="stack" onSubmit={submit}>
        <div className="form-grid">
          <Field label="Название"><Input value={name} onChange={(event) => setName(event.target.value)} /></Field>
          <Field label="Описание"><Input value={description} onChange={(event) => setDescription(event.target.value)} /></Field>
        </div>
        <label className="checkbox-row"><input type="checkbox" checked={isDefault} onChange={(event: ChangeEvent<HTMLInputElement>) => setIsDefault(event.target.checked)} /><span>Сделать профилем по умолчанию</span></label>
        <Field label="Конфигурация JSON" hint="Все параметры профиля сохраняются как отдельная версия." error={error || undefined}>
          <Textarea className="code-editor" rows={22} value={json} onChange={(event) => setJson(event.target.value)} spellCheck={false} />
        </Field>
      </form>
    </Modal>
  );
}

function EditProfileModal({
  open,
  profile,
  onClose,
  onSave,
}: {
  open: boolean;
  profile: RetrievalProfileDetailRead | null;
  onClose: () => void;
  onSave: (payload: RetrievalProfileUpdate) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [json, setJson] = useState("{}");
  const [originalJson, setOriginalJson] = useState("{}");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    const formatted = JSON.stringify(profile.latest_version.config, null, 2);
    setName(profile.name);
    setDescription(profile.description ?? "");
    setIsDefault(profile.is_default);
    setIsActive(profile.is_active);
    setJson(formatted);
    setOriginalJson(formatted);
    setError("");
  }, [profile]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const config = parseConfig(json, setError);
    if (!config) return;
    const payload: RetrievalProfileUpdate = {
      name: name.trim(),
      description: description.trim() || null,
      is_default: isDefault,
      is_active: isActive,
    };
    if (json.trim() !== originalJson.trim()) payload.config = config;
    setSaving(true);
    try {
      await onSave(payload);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Не удалось сохранить профиль");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open && Boolean(profile)} title="Редактировать профиль" onClose={onClose} wide footer={<><Button variant="secondary" onClick={onClose}>Отмена</Button><Button form="edit-profile-form" type="submit" disabled={!name.trim() || saving}>{saving ? "Сохраняем…" : "Сохранить"}</Button></>}>
      <form id="edit-profile-form" className="stack" onSubmit={submit}>
        <div className="form-grid">
          <Field label="Название"><Input value={name} onChange={(event) => setName(event.target.value)} /></Field>
          <Field label="Описание"><Input value={description} onChange={(event) => setDescription(event.target.value)} /></Field>
        </div>
        <div className="inline-options">
          <label className="checkbox-row"><input type="checkbox" checked={isDefault} onChange={(event: ChangeEvent<HTMLInputElement>) => setIsDefault(event.target.checked)} /><span>Профиль по умолчанию</span></label>
          <label className="checkbox-row"><input type="checkbox" checked={isActive} onChange={(event: ChangeEvent<HTMLInputElement>) => setIsActive(event.target.checked)} /><span>Профиль активен</span></label>
        </div>
        <Callout tone="info">Изменение JSON создаст новую версию профиля. Изменение названия, описания или статуса не создаёт новую версию конфигурации.</Callout>
        <Field label="Конфигурация JSON" error={error || undefined}>
          <Textarea className="code-editor" rows={22} value={json} onChange={(event) => setJson(event.target.value)} spellCheck={false} />
        </Field>
      </form>
    </Modal>
  );
}

function parseConfig(json: string, setError: (value: string) => void): JsonObject | null {
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error();
    return parsed as JsonObject;
  } catch {
    setError("Конфигурация должна быть корректным JSON-объектом.");
    return null;
  }
}
